import React, { useEffect, useMemo, useState } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import { API_URL } from "../../../config";
import { getCurrencySymbol } from "../../../utils/currency";
import "./CreateOrderModal.css";



const STEPS = ["Products & Modules", "Billing & Shipping", "Review & Confirm"];

const SENSOR_PRODUCT_ID = "136";
const WARRANTY_PRODUCT_ID = "137";
const ACCESSORY_PRODUCT_IDS = [SENSOR_PRODUCT_ID, WARRANTY_PRODUCT_ID];
const ESPORT_PRODUCT_ID = "131"; // "available Oct 2026" on the storefront — not orderable yet


const ADVANCED_MODULE_ENUMS = ["ADVANCED_FEATURES_MODULE", "CELL_PHONE_MODULE", "PRACTICUMS_MODULE", "CLIENTS_MODULE"];
const PROFESSIONAL_MODULE_ENUMS = [...ADVANCED_MODULE_ENUMS, "MANAGEMENT_MODULE", "TRAINER_MODULE", "HRV_MODULE"];
const GROUP_SYSTEM_MODULE_ENUMS = ["ADVANCED_FEATURES_MODULE", "CLIENTS_MODULE", "GROUP_MODULE", "MANAGEMENT_MODULE"];

const MODULE_LINKS = [
  { label: "Advanced Features", keyword: "advanced" },
  { label: "Basic", keyword: "basic", always: true },
  { label: "Cellphone", keyword: "cellphone" },
  { label: "Clients", keyword: "client" },
  { label: "E-sports", keyword: "e-sport", unavailable: true },
  { label: "Groupwork", keyword: "group" },
  { label: "HRV (Heart Rate Variability)", keyword: "hrv" },
  { label: "Management", keyword: "management" },
  { label: "Muscles (EMG)", keyword: "muscle" },
  { label: "Practicums", keyword: "practicum" },
  { label: "Reports", keyword: "report", always: true },
  { label: "Trainers", keyword: "trainer" },
];

const EMPTY_ADDRESS = {
  fname: "",
  lname: "",
  company: "",
  address_1: "",
  address_2: "",
  city: "",
  postal: "",
  phone: "",
  email: "",
  countryId: "",
  stateId: "",
};

const normalize = (value) => String(value || "").toLowerCase().replace(/\s+/g, "");

// The storefront saves US states as 2-letter codes (and other countries by
// name), so codes are mapped back to names to pick the state from the list.
const US_STATE_CODE_TO_NAME = {
  AL: "Alabama", AK: "Alaska", AZ: "Arizona", AR: "Arkansas", CA: "California",
  CO: "Colorado", CT: "Connecticut", DE: "Delaware", DC: "District of Columbia",
  FL: "Florida", GA: "Georgia", HI: "Hawaii", ID: "Idaho", IL: "Illinois",
  IN: "Indiana", IA: "Iowa", KS: "Kansas", KY: "Kentucky", LA: "Louisiana",
  ME: "Maine", MD: "Maryland", MA: "Massachusetts", MI: "Michigan", MN: "Minnesota",
  MS: "Mississippi", MO: "Missouri", MT: "Montana", NE: "Nebraska", NV: "Nevada",
  NH: "New Hampshire", NJ: "New Jersey", NM: "New Mexico", NY: "New York",
  NC: "North Carolina", ND: "North Dakota", OH: "Ohio", OK: "Oklahoma", OR: "Oregon",
  PA: "Pennsylvania", RI: "Rhode Island", SC: "South Carolina", SD: "South Dakota",
  TN: "Tennessee", TX: "Texas", UT: "Utah", VT: "Vermont", VA: "Virginia",
  WA: "Washington", WV: "West Virginia", WI: "Wisconsin", WY: "Wyoming",
};
const US_STATE_NAME_TO_CODE = Object.fromEntries(
  Object.entries(US_STATE_CODE_TO_NAME).map(([code, name]) => [name, code])
);

const parseAddress = (value) => {
  if (!value) return null;
  if (typeof value === "object") return value;
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};

const apiRequest = async (path, { method = "GET", body, auth = false } = {}) => {
  const headers = { "Content-Type": "application/json" };
  if (auth) headers["x-access-token"] = localStorage.getItem("accessToken");
  const res = await fetch(`${API_URL}${path}`, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });
  const data = await res.json().catch(() => ({}));
  return { ok: res.ok && data.status !== false, status: res.status, data };
};

const distributorLabel = (d) =>
  `${d.firstname || ""} ${d.lastname || ""}`.trim() || d.business || `Distributor #${d.id}`;

const CreateOrderModal = ({ isOpen, toggle, isAdmin, userId, distributors, defaultDistributorId, onCreated }) => {
  const [step, setStep] = useState(0);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  // Store
  const [distributorId, setDistributorId] = useState("");
  const [sites, setSites] = useState([]);
  const [siteId, setSiteId] = useState("");
  const [currency, setCurrency] = useState("USD");

  // Catalogue
  const [products, setProducts] = useState([]);
  const [modules, setModules] = useState([]);
  const [modulePrice, setModulePrice] = useState(0);
  const [stock, setStock] = useState({ device: null, module: null });
  const [loadingCatalogue, setLoadingCatalogue] = useState(false);

  // Cart (anonymous cart user id, same as the storefront's localStorage "user")
  const [cartUserId, setCartUserId] = useState("");
  const [cartItems, setCartItems] = useState([]);
  const [quantities, setQuantities] = useState({});

  // Addresses
  const [countries, setCountries] = useState([]);
  const [billingStates, setBillingStates] = useState([]);
  const [shippingStates, setShippingStates] = useState([]);
  const [billing, setBilling] = useState(EMPTY_ADDRESS);
  const [shipping, setShipping] = useState(EMPTY_ADDRESS);
  const [shipDifferent, setShipDifferent] = useState(false);

  // Existing customers of the selected distributor (taken from its past
  // orders), used to prefill billing/shipping.
  const [customers, setCustomers] = useState([]);
  const [customersFor, setCustomersFor] = useState("");
  const [customerQuery, setCustomerQuery] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  // State names waiting for that country's state list to load.
  const [pendingStates, setPendingStates] = useState({ billing: "", shipping: "" });

  // Order
  const [order, setOrder] = useState(null);
  const [confirmedOrderId, setConfirmedOrderId] = useState(null);
  // Entered by the distributor/admin on the review step (not the site's
  // configured shipping rates).
  const [shippingFee, setShippingFee] = useState("");
  const [taxAmount, setTaxAmount] = useState("");

  const toAmount = (value) => {
    const n = Number(value);
    return Number.isFinite(n) && n > 0 ? n : 0;
  };
  const isValidAmount = (value) => value === "" || (Number.isFinite(Number(value)) && Number(value) >= 0);

  const symbol = getCurrencySymbol(currency);
  const money = (value) => `${symbol}${Number(value || 0).toFixed(2)}`;

  // ───────── open / reset ─────────

  const resetAll = () => {
    setStep(0);
    setError("");
    setBusy(false);
    setSites([]);
    setSiteId("");
    setProducts([]);
    setCartUserId("");
    setCartItems([]);
    setQuantities({});
    setBilling(EMPTY_ADDRESS);
    setShipping(EMPTY_ADDRESS);
    setShipDifferent(false);
    setOrder(null);
    setConfirmedOrderId(null);
    setShippingFee("");
    setTaxAmount("");
    // Reload customers each time the modal opens (a new order may have added one).
    setCustomers([]);
    setCustomersFor("");
    setCustomerQuery("");
    setPendingStates({ billing: "", shipping: "" });
  };

  useEffect(() => {
    if (!isOpen) return;
    resetAll();
    setDistributorId(isAdmin ? defaultDistributorId || "" : userId);
    loadModules();
    loadCountries();
  }, [isOpen]);

  const loadModules = async () => {
    const { data } = await apiRequest("/get/all/modules");
    setModules(data.modules || data.data || []);
  };

  const loadCountries = async () => {
    const { data } = await apiRequest("/countries");
    // The countries table uses sortname/Name, and "USA" for the US row —
    // normalized the same way the storefront checkout does.
    const list = (data.countries || []).map((c) => {
      const code = c.code || c.sortname || "";
      return { id: String(c.id), code: code === "USA" ? "US" : code, name: c.name || c.Name || "" };
    });
    list.sort((a, b) => (a.code === "US" ? -1 : b.code === "US" ? 1 : a.name.localeCompare(b.name)));
    setCountries(list);
  };

  // ───────── store (distributor + site) ─────────

  useEffect(() => {
    if (!isOpen || !distributorId) {
      setSites([]);
      setSiteId("");
      return;
    }
    (async () => {
      const { data } = await apiRequest(`/distributor/site/${distributorId}`, { auth: true });
      const list = Array.isArray(data.data) ? data.data : [];
      setSites(list);
      // Prefer the distributor's active site (the one chosen in the site
      // switcher, which is also where Products / Mini Cart manage custom
      // products and prices), then "Default", then the first site.
      const activeSiteId = isAdmin ? null : localStorage.getItem("site_id");
      const preferred =
        list.find((s) => s.site_id === activeSiteId) ||
        list.find((s) => s.name === "Default") ||
        list[0];
      setSiteId(preferred ? preferred.site_id : "");
    })();
  }, [isOpen, distributorId]);

  useEffect(() => {
    if (!isOpen || !siteId) {
      setProducts([]);
      return;
    }
    loadCatalogue();
  }, [isOpen, siteId]);

  const loadCatalogue = async () => {
    setLoadingCatalogue(true);
    try {
      const [systems, accessories, price, detail, txn] = await Promise.all([
        apiRequest(`/get/system/product/22/${siteId}`),
        apiRequest(`/get/system/product/24/${siteId}`),
        apiRequest(`/distributor/module/price/${siteId}`),
        apiRequest(`/get/distributer/by/site/${siteId}`),
        apiRequest(`/txn/${distributorId}`),
      ]);
      const list = [
        ...(systems.data.data || []).map((p) => ({ ...p, isAccessory: false })),
        ...(accessories.data.data || []).map((p) => ({ ...p, isAccessory: true })),
      ]
        .map((p) => ({ ...p, isAccessory: p.isAccessory || ACCESSORY_PRODUCT_IDS.includes(String(p.id)) }))
        .filter((p) => Number(p.price) > 0)
        .map((p, index) => ({ ...p, isCustom: !!p.distributor_id, sortIndex: index }))
        // Standard products first, then the distributor's own (custom)
        // products; the API's order is kept within each group.
        .sort((a, b) => a.isCustom - b.isCustom || a.sortIndex - b.sortIndex);
      setProducts(list);
      setModulePrice(Number(price.data.module_price || 0));
      setCurrency(detail.data?.data?.selected_currency || "USD");
      setStock({
        device: txn.data.device?.[0]?.available ?? null,
        module: txn.data.module?.[0]?.available ?? null,
      });
    } finally {
      setLoadingCatalogue(false);
    }
  };

  const clearCart = async (items = cartItems) => {
    await Promise.all(
      items.map((item) => fetch(`${API_URL}/delete/cart/by/${item.id}`, { method: "DELETE" }))
    );
    setCartItems([]);
  };

  const changeDistributor = async (value) => {
    if (cartItems.length) await clearCart();
    setDistributorId(value);
  };

  const changeSite = async (value) => {
    if (cartItems.length) await clearCart();
    setSiteId(value);
  };

  // ───────── cart ─────────

  const refreshCart = async (uid = cartUserId) => {
    if (!uid) return setCartItems([]);
    const { data } = await apiRequest(`/get-cart/by/${uid}`);
    setCartItems(Array.isArray(data.data) ? data.data : []);
  };

  const productById = (id) => products.find((p) => String(p.id) === String(id));
  const cartItemFor = (productId) => cartItems.find((c) => String(c.product_id) === String(productId));

  const mainCartItem = cartItems.find((c) => {
    const product = productById(c.product_id);
    return product ? !product.isAccessory : !ACCESSORY_PRODUCT_IDS.includes(String(c.product_id));
  });
  const mainProduct = mainCartItem ? productById(mainCartItem.product_id) : null;
  const mainModuleIds = mainCartItem?.moduleIds
    ? mainCartItem.moduleIds.split(",").map((id) => id.trim()).filter(Boolean)
    : [];

  const findModule = (keyword) => modules.find((m) => normalize(m.name).includes(normalize(keyword)));

  const bundledModulesFor = (product) => {
    const id = String(product.id);
    if (id === "133") return modules.filter((m) => PROFESSIONAL_MODULE_ENUMS.includes(m.module_enum));
    if (id === "134") return modules.filter((m) => GROUP_SYSTEM_MODULE_ENUMS.includes(m.module_enum));
    if (id === "132") return modules.filter((m) => ADVANCED_MODULE_ENUMS.includes(m.module_enum));
    if (product.module_id) {
      const ids = product.module_id.split(",").map((x) => x.trim()).filter(Boolean);
      return modules.filter((m) => ids.includes(String(m.id)));
    }
    return [];
  };

  const runCartAction = async (fn) => {
    setBusy(true);
    setError("");
    try {
      await fn();
    } catch (err) {
      setError(err.message || "Something went wrong");
    } finally {
      setBusy(false);
    }
  };

  const addToCart = (product) =>
    runCartAction(async () => {
      const qty = quantities[product.id] || 0;
      if (qty < 1) return;
      const isWarranty = String(product.id) === WARRANTY_PRODUCT_ID;

      if (!product.isAccessory && mainCartItem && String(mainCartItem.product_id) !== String(product.id)) {
        throw new Error(
          `Only one system can be ordered at a time. Remove "${mainProduct?.name || "the current system"}" first.`
        );
      }
      if (isWarranty) {
        const deviceQty = mainCartItem?.quantity || 0;
        const already = cartItemFor(product.id)?.quantity || 0;
        if (deviceQty === 0) throw new Error("Add a system before adding an extended warranty.");
        if (already + qty > deviceQty) throw new Error("Extended warranty quantity cannot exceed the system quantity.");
      }

      const moduleIds = bundledModulesFor(product).map((m) => m.id);
      const { ok, data } = await apiRequest("/distributor/add-to-cart", {
        method: "POST",
        body: { user_id: cartUserId, product_ids: [product.id], moduleIds, quantities: [qty], site_id: siteId },
      });
      if (!ok) throw new Error(data.message || "Could not add the product");
      setCartUserId(data.user_id);
      setQuantities((q) => ({ ...q, [product.id]: 0 }));
      await refreshCart(data.user_id);
    });

  const changeCartQty = (item, delta) =>
    runCartAction(async () => {
      const newQty = item.quantity + delta;
      if (newQty < 1) return;
      if (delta > 0 && String(item.product_id) === WARRANTY_PRODUCT_ID && newQty > (mainCartItem?.quantity || 0)) {
        throw new Error("Extended warranty quantity cannot exceed the system quantity.");
      }
      const { ok, data } = await apiRequest(`/distributor/update/cart/by/${cartUserId}`, {
        method: "PUT",
        body: { cart_ids: [item.id], quantities: [delta], site_id: siteId },
      });
      if (!ok) throw new Error(data.message || "Could not update the quantity");
      await refreshCart();
    });

  const removeFromCart = (item) =>
    runCartAction(async () => {
      const removed = [item];
      // Same as the storefront: removing the system also removes its warranty.
      if (mainCartItem && item.id === mainCartItem.id) {
        removed.push(...cartItems.filter((c) => String(c.product_id) === WARRANTY_PRODUCT_ID));
      }
      await Promise.all(removed.map((c) => fetch(`${API_URL}/delete/cart/by/${c.id}`, { method: "DELETE" })));
      await refreshCart();
    });

  const saveModules = (moduleIds) =>
    runCartAction(async () => {
      const { ok, data } = await apiRequest("/distributor/cart/update-modules", {
        method: "POST",
        body: { user_id: cartUserId, product_id: mainCartItem.product_id, moduleIds, site_id: siteId },
      });
      if (!ok) throw new Error(data.message || "Could not update modules");
      await refreshCart();
    });

  const toggleModule = (link, apiModule, checked) => {
    const id = String(apiModule.id);
    if (!checked && (link.keyword === "group" || link.keyword === "trainer")) {
      const management = findModule("management");
      if (!management || !mainModuleIds.includes(String(management.id))) {
        setError("Select the Management module first — Groupwork and Trainers require it.");
        return;
      }
    }
    if (checked && link.keyword === "management") {
      const dependents = [findModule("group"), findModule("trainer")].filter(Boolean).map((m) => String(m.id));
      saveModules(mainModuleIds.filter((x) => x !== id && !dependents.includes(x)));
      return;
    }
    saveModules(checked ? mainModuleIds.filter((x) => x !== id) : [...mainModuleIds, id]);
  };

  const isIncludedForMainProduct = (apiModule) =>
    !!mainProduct && bundledModulesFor(mainProduct).some((m) => String(m.id) === String(apiModule.id));

  const cartTotal = cartItems.reduce((sum, item) => sum + Number(item.total_price || 0), 0);

  // ───────── addresses ─────────

  useEffect(() => {
    if (!billing.countryId) return setBillingStates([]);
    apiRequest(`/states?country_id=${billing.countryId}`).then(({ data }) => setBillingStates(data.states || []));
  }, [billing.countryId]);

  useEffect(() => {
    if (!shipping.countryId) return setShippingStates([]);
    apiRequest(`/states?country_id=${shipping.countryId}`).then(({ data }) => setShippingStates(data.states || []));
  }, [shipping.countryId]);

  const findStateId = (states, stateValue) => {
    const wanted = String(stateValue || "").trim().toLowerCase();
    if (!wanted) return "";
    const name = (US_STATE_CODE_TO_NAME[stateValue.toUpperCase()] || stateValue).toLowerCase();
    const match = states.find((s) => s.name.toLowerCase() === name || s.name.toLowerCase() === wanted);
    return match ? String(match.id) : "";
  };

  // Once a prefilled country's states arrive, select the customer's state.
  useEffect(() => {
    if (!pendingStates.billing || billingStates.length === 0) return;
    setBilling((prev) => ({ ...prev, stateId: findStateId(billingStates, pendingStates.billing) }));
    setPendingStates((prev) => ({ ...prev, billing: "" }));
  }, [billingStates, pendingStates.billing]);

  useEffect(() => {
    if (!pendingStates.shipping || shippingStates.length === 0) return;
    setShipping((prev) => ({ ...prev, stateId: findStateId(shippingStates, pendingStates.shipping) }));
    setPendingStates((prev) => ({ ...prev, shipping: "" }));
  }, [shippingStates, pendingStates.shipping]);

  // Customers = unique billing contacts from the distributor's past orders,
  // newest order first, each with its latest billing and shipping address.
  useEffect(() => {
    if (!isOpen || step !== 1 || !distributorId || customersFor === distributorId) return;
    (async () => {
      const { data } = await apiRequest(
        `/get/distributor/all-order?distributor_id=${distributorId}`,
        { auth: true }
      );
      const orders = [...(data.items || [])].sort((a, b) => new Date(b.created) - new Date(a.created));
      const seen = new Set();
      const list = [];
      for (const o of orders) {
        const billingAddress = parseAddress(o.billing_address_info);
        if (!billingAddress || !(billingAddress.fname || billingAddress.lname || billingAddress.email)) continue;
        const key = `${normalize(billingAddress.fname)}|${normalize(billingAddress.lname)}|${normalize(billingAddress.email)}`;
        if (seen.has(key)) continue;
        seen.add(key);
        list.push({ key, billing: billingAddress, shipping: parseAddress(o.shipping_address_info) });
      }
      setCustomers(list);
      setCustomersFor(distributorId);
    })();
  }, [isOpen, step, distributorId, customersFor]);

  const customerSuggestions = customerQuery.trim()
    ? customers
        .filter((c) =>
          [`${c.billing.fname || ""} ${c.billing.lname || ""}`, c.billing.email, c.billing.company]
            .some((v) => String(v || "").toLowerCase().includes(customerQuery.trim().toLowerCase()))
        )
        .slice(0, 8)
    : [];

  const toFormAddress = (info) => {
    const country = countries.find((c) => c.code === String(info.country || "").toUpperCase());
    return {
      fname: info.fname || "",
      lname: info.lname || "",
      company: info.company || "",
      address_1: info.address_1 || "",
      address_2: info.address_2 || "",
      city: info.city || "",
      postal: info.postal || "",
      phone: info.phone || "",
      email: info.email || "",
      countryId: country ? country.id : "",
      stateId: "",
    };
  };

  const applyCustomer = (customer) => {
    const billingForm = toFormAddress(customer.billing);
    setBilling(billingForm);
    const shippingInfo = customer.shipping;
    const sameAddress =
      !shippingInfo ||
      ["fname", "lname", "address_1", "city", "postal", "country"].every(
        (k) => normalize(shippingInfo[k]) === normalize(customer.billing[k])
      );
    if (sameAddress) {
      setShipDifferent(false);
      setShipping(EMPTY_ADDRESS);
    } else {
      setShipDifferent(true);
      setShipping(toFormAddress(shippingInfo));
    }
    setPendingStates({
      billing: customer.billing.state || "",
      shipping: sameAddress ? "" : shippingInfo.state || "",
    });
    setCustomerQuery(`${customer.billing.fname || ""} ${customer.billing.lname || ""}`.trim());
    setShowSuggestions(false);
    setError("");
  };

  const toAddressInfo = (address, states) => {
    const country = countries.find((c) => c.id === address.countryId);
    const state = states.find((s) => String(s.id) === String(address.stateId));
    return {
      fname: address.fname,
      lname: address.lname,
      company: address.company,
      country: country?.code,
      // Same format as storefront orders: US states as 2-letter codes.
      state: state ? (country?.code === "US" ? US_STATE_NAME_TO_CODE[state.name] || state.name : state.name) : undefined,
      address_1: address.address_1,
      address_2: address.address_2,
      city: address.city,
      postal: address.postal,
      phone: address.phone,
      email: address.email,
    };
  };

  const billingInfo = useMemo(() => toAddressInfo(billing, billingStates), [billing, billingStates, countries]);
  const shippingInfo = useMemo(
    () => (shipDifferent ? toAddressInfo(shipping, shippingStates) : billingInfo),
    [shipDifferent, shipping, shippingStates, billingInfo, countries]
  );

  const makeOrder = () =>
    runCartAction(async () => {
      const required = ["fname", "lname", "address_1", "city", "postal", "phone", "email", "countryId"];
      if (required.some((k) => !String(billing[k]).trim())) {
        throw new Error("Please fill in all required billing fields.");
      }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(billing.email.trim())) {
        throw new Error("Enter a valid billing email.");
      }
      if (shipDifferent && ["fname", "lname", "address_1", "city", "countryId"].some((k) => !String(shipping[k]).trim())) {
        throw new Error("Please fill in all required shipping fields.");
      }
      const { ok, data } = await apiRequest("/distributor/shopping-cart/make-order", {
        method: "POST",
        body: {
          user_id: cartUserId,
          shipping_address_info: shippingInfo,
          billing_address_info: billingInfo,
          site_id: siteId,
        },
      });
      if (!ok) throw new Error(data.message || "Could not create the order");
      setOrder({
        id: data.order_id,
        subtotal: data.total_product_price,
        shipping: data.total_shippingRate,
        total: data.total_price_with_shipping,
      });
      setStep(2);
    });

  const confirmOrder = () =>
    runCartAction(async () => {
      if (!isValidAmount(shippingFee) || !isValidAmount(taxAmount)) {
        throw new Error("Shipping and tax must be 0 or a positive amount.");
      }
      const { ok, data } = await apiRequest("/distributor/shopping-cart/confirm-order-without-payment", {
        method: "PUT",
        auth: true,
        body: {
          order_id: order.id,
          site_id: siteId,
          shipping_fee: toAmount(shippingFee),
          tax_amount: toAmount(taxAmount),
        },
      });
      if (!ok) throw new Error(data.message || "Could not confirm the order");
      setConfirmedOrderId(order.id);
      setCartItems([]);
      setStep(3);
      if (onCreated) onCreated();
    });

  // ───────── close ─────────

  const handleClose = async () => {
    if (busy) return;
    // An unfinished order's cart rows are dropped so they don't linger.
    if (!confirmedOrderId && cartItems.length) await clearCart();
    toggle();
  };

  // ───────── render ─────────

  const renderAddressFields = (address, setAddress, states, title) => {
    const set = (key) => (e) => {
      const value = e.target.value;
      setAddress((prev) => ({ ...prev, [key]: value, ...(key === "countryId" ? { stateId: "" } : {}) }));
      setError("");
    };
    return (
      <div className="co-card">
        <h4 className="co-card-title">{title}</h4>
        <div className="co-grid-2">
          <input placeholder="First name*" value={address.fname} onChange={set("fname")} />
          <input placeholder="Last name*" value={address.lname} onChange={set("lname")} />
          <input placeholder="Company (optional)" value={address.company} onChange={set("company")} />
          <select value={address.countryId} onChange={set("countryId")}>
            <option value="">Select country*</option>
            {countries.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
          <input className="co-span-2" placeholder="Address line 1*" value={address.address_1} onChange={set("address_1")} />
          <input className="co-span-2" placeholder="Address line 2" value={address.address_2} onChange={set("address_2")} />
          <input placeholder="City / District*" value={address.city} onChange={set("city")} />
          <select value={address.stateId} onChange={set("stateId")} disabled={!address.countryId}>
            <option value="">State / Province</option>
            {states.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
          <input placeholder="Postal code*" value={address.postal} onChange={set("postal")} />
          <input placeholder="Phone*" value={address.phone} onChange={set("phone")} />
          <input className="co-span-2" type="email" placeholder="Email*" value={address.email} onChange={set("email")} />
        </div>
      </div>
    );
  };

  const renderAddressSummary = (info, title) => (
    <div className="co-card">
      <h4 className="co-card-title">{title}</h4>
      <p className="co-address">
        {info.fname} {info.lname}
        {info.company ? `, ${info.company}` : ""}
        <br />
        {info.address_1}
        {info.address_2 ? `, ${info.address_2}` : ""}
        <br />
        {[info.city, info.state, info.postal, info.country].filter(Boolean).join(", ")}
        <br />
        {info.phone} · {info.email}
      </p>
    </div>
  );

  const renderProductRow = (product) => {
    const inCart = cartItemFor(product.id);
    const isEsport = String(product.id) === ESPORT_PRODUCT_ID;
    const qty = quantities[product.id] || 0;
    return (
      <div className="co-product-row" key={product.id}>
        <div className="co-product-info">
          <span className="co-product-name">{product.name}</span>
          {isEsport && <span className="co-note">Available Oct 2026</span>}
        </div>
        <span className="co-price">{money(product.price)}</span>
        {inCart ? (
          <div className="co-product-actions">
            <div className="co-qty">
              <button type="button" onClick={() => changeCartQty(inCart, -1)} disabled={busy || inCart.quantity <= 1}>
                −
              </button>
              <span>{inCart.quantity}</span>
              <button type="button" onClick={() => changeCartQty(inCart, 1)} disabled={busy}>
                +
              </button>
            </div>
            <button type="button" className="co-remove-btn" title="Remove" onClick={() => removeFromCart(inCart)} disabled={busy}>
              <i className="fa fa-trash"></i>
            </button>
          </div>
        ) : (
          <div className="co-product-actions">
            <div className="co-qty">
              <button
                type="button"
                onClick={() => setQuantities((q) => ({ ...q, [product.id]: Math.max(0, qty - 1) }))}
                disabled={busy || isEsport || qty <= 0}
              >
                −
              </button>
              <span>{qty}</span>
              <button
                type="button"
                onClick={() => setQuantities((q) => ({ ...q, [product.id]: qty + 1 }))}
                disabled={busy || isEsport}
              >
                +
              </button>
            </div>
            <button type="button" className="co-add-btn" onClick={() => addToCart(product)} disabled={busy || isEsport || qty === 0}>
              Add
            </button>
          </div>
        )}
      </div>
    );
  };

  const systems = products.filter((p) => !p.isAccessory);
  const accessories = products.filter((p) => p.isAccessory);

  const renderProductsStep = () => (
    <>
      <div className="co-store-bar">
        {isAdmin && (
          <label>
            Distributor
            <select value={distributorId} onChange={(e) => changeDistributor(e.target.value)} disabled={busy}>
              <option value="">Select distributor</option>
              {distributors.map((d) => (
                <option key={d.id} value={d.distributor_id}>
                  {distributorLabel(d)}
                </option>
              ))}
            </select>
          </label>
        )}
        <label>
          Site
          <select value={siteId} onChange={(e) => changeSite(e.target.value)} disabled={busy || sites.length === 0}>
            {sites.length === 0 && <option value="">No sites</option>}
            {sites.map((s) => (
              <option key={s.site_id} value={s.site_id}>
                {s.name}
              </option>
            ))}
          </select>
        </label>
        {(stock.device !== null || stock.module !== null) && siteId && (
          <span className="co-stock">
            Stock: {stock.device ?? 0} systems · {stock.module ?? 0} modules
          </span>
        )}
      </div>

      {!siteId ? (
        <div className="co-empty">{isAdmin ? "Select a distributor to load its products." : "No site found for this account."}</div>
      ) : loadingCatalogue ? (
        <div className="co-empty">Loading products...</div>
      ) : (
        <div className="co-products-layout">
          <div>
            <h4 className="co-section-title">CapnoGO™ Systems</h4>
            {systems.length === 0 ? <div className="co-empty">No priced systems for this site.</div> : systems.map(renderProductRow)}
            {accessories.length > 0 && (
              <>
                <h4 className="co-section-title">Accessories</h4>
                {accessories.map(renderProductRow)}
              </>
            )}
          </div>

          <div className={`co-modules ${mainCartItem ? "" : "co-modules-disabled"}`}>
            <h4 className="co-section-title">
              CapnoModules™ <small>(all application modules {money(modulePrice)})</small>
            </h4>
            {!mainCartItem && <div className="co-hint">Add a system first to select modules.</div>}
            {MODULE_LINKS.map((link) => {
              const apiModule = findModule(link.keyword);
              const included = apiModule ? isIncludedForMainProduct(apiModule) : false;
              const checked = link.always || (apiModule ? mainModuleIds.includes(String(apiModule.id)) : false);
              const disabled = !mainCartItem || busy || link.always || link.unavailable || included || !apiModule;
              return (
                <label key={link.label} className={`co-module ${disabled ? "is-disabled" : ""}`}>
                  <input
                    type="checkbox"
                    checked={!!mainCartItem && checked}
                    disabled={disabled}
                    onChange={() => apiModule && toggleModule(link, apiModule, checked)}
                  />
                  {link.label}
                  {link.always && <span className="co-note">Included in all systems</span>}
                  {included && !link.always && <span className="co-note">Included</span>}
                  {link.unavailable && <span className="co-note">Available Oct 2026</span>}
                </label>
              );
            })}
          </div>
        </div>
      )}
    </>
  );

  // Per-module lines, priced the way the API's calculateTotalPrice does it:
  // modules bundled with the product are free, every other module costs the
  // site's module price. The cart row's module_unit_price is the per-unit sum
  // of the chargeable ones, so it is split evenly across them.
  const moduleLinesFor = (item) => {
    const product = productById(item.product_id);
    const ids = item.moduleIds ? item.moduleIds.split(",").map((id) => id.trim()).filter(Boolean) : [];
    const includedIds = product ? bundledModulesFor(product).map((m) => String(m.id)) : [];
    const chargeableCount = ids.filter((id) => !includedIds.includes(id)).length;
    const perModule = chargeableCount > 0 ? Number(item.module_unit_price || 0) / chargeableCount : 0;
    return ids.map((id) => {
      const included = includedIds.includes(id);
      return {
        id,
        name: modules.find((m) => String(m.id) === id)?.name || `Module #${id}`,
        included,
        unitPrice: included ? 0 : perModule,
      };
    });
  };

  const renderCartTable = () => (
    <table className="co-cart-table">
      <thead>
        <tr>
          <th>Item</th>
          <th className="co-right">Unit price</th>
          <th className="co-right">Qty</th>
          <th className="co-right">Total</th>
        </tr>
      </thead>
      <tbody>
        {cartItems.map((item) => {
          const qty = Number(item.quantity || 0);
          const unitPrice = Number(item.unit_price || 0);
          return (
            <React.Fragment key={item.id}>
              <tr className="co-product-line">
                <td>{productById(item.product_id)?.name || `Product #${item.product_id}`}</td>
                <td className="co-right">{money(unitPrice)}</td>
                <td className="co-right">{qty}</td>
                <td className="co-right">{money(unitPrice * qty)}</td>
              </tr>
              {moduleLinesFor(item).map((line) => (
                <tr key={line.id} className="co-module-line">
                  <td>+ {line.name}</td>
                  <td className="co-right">{line.included ? "Included" : money(line.unitPrice)}</td>
                  <td className="co-right">{qty}</td>
                  <td className="co-right">{line.included ? money(0) : money(line.unitPrice * qty)}</td>
                </tr>
              ))}
            </React.Fragment>
          );
        })}
      </tbody>
    </table>
  );

  const renderBody = () => {
    if (step === 3) {
      return (
        <div className="co-success">
          <i className="fas fa-check-circle"></i>
          <h3>Order #{confirmedOrderId} confirmed</h3>
          <p>The order was created without payment and now appears in the Active orders list.</p>
        </div>
      );
    }
    if (step === 0) return renderProductsStep();
    if (step === 1) {
      return (
        <>
          <div className="co-customer-search">
            <label htmlFor="co-customer-search">Existing customer</label>
            <input
              id="co-customer-search"
              type="text"
              autoComplete="off"
              placeholder={customers.length ? "Search by name or email" : "No previous customers found"}
              value={customerQuery}
              disabled={customers.length === 0}
              onChange={(e) => {
                setCustomerQuery(e.target.value);
                setShowSuggestions(true);
              }}
              onFocus={() => setShowSuggestions(true)}
              onBlur={() => setShowSuggestions(false)}
            />
            {showSuggestions && customerQuery.trim() && (
              <ul className="co-suggestions">
                {customerSuggestions.length === 0 ? (
                  <li className="co-suggestion-empty">No matching customer</li>
                ) : (
                  customerSuggestions.map((c) => (
                    <li
                      key={c.key}
                      // onMouseDown runs before the input's blur closes the list
                      onMouseDown={(e) => {
                        e.preventDefault();
                        applyCustomer(c);
                      }}
                    >
                      <strong>
                        {`${c.billing.fname || ""} ${c.billing.lname || ""}`.trim() || c.billing.email}
                      </strong>
                      <span>
                        {[c.billing.email, c.billing.city, c.billing.country].filter(Boolean).join(" · ")}
                      </span>
                    </li>
                  ))
                )}
              </ul>
            )}
          </div>
          {renderAddressFields(billing, setBilling, billingStates, "Billing details")}
          <label className="co-check">
            <input type="checkbox" checked={shipDifferent} onChange={(e) => setShipDifferent(e.target.checked)} />
            Ship to a different address
          </label>
          {shipDifferent && renderAddressFields(shipping, setShipping, shippingStates, "Shipping details")}
        </>
      );
    }
    return (
      <>
        <div className="co-grid-2 co-addresses">
          {renderAddressSummary(billingInfo, "Billing to")}
          {renderAddressSummary(shippingInfo, "Shipping to")}
        </div>
        <div className="co-card">
          <h4 className="co-card-title">Order summary</h4>
          {renderCartTable()}
          <div className="co-totals">
            <div>
              <span>Subtotal</span>
              <span>{money(order?.subtotal)}</span>
            </div>
            <div className="co-amount-row">
              <label htmlFor="co-shipping-fee">Shipping</label>
              <div className="co-amount-input">
                <span>{symbol}</span>
                <input
                  id="co-shipping-fee"
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="0.00"
                  value={shippingFee}
                  onChange={(e) => setShippingFee(e.target.value)}
                />
              </div>
            </div>
            <div className="co-amount-row">
              <label htmlFor="co-tax-amount">Tax</label>
              <div className="co-amount-input">
                <span>{symbol}</span>
                <input
                  id="co-tax-amount"
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="0.00"
                  value={taxAmount}
                  onChange={(e) => setTaxAmount(e.target.value)}
                />
              </div>
            </div>
            <div className="co-grand-total">
              <span>Total</span>
              <span>{money(Number(order?.subtotal || 0) + toAmount(shippingFee) + toAmount(taxAmount))}</span>
            </div>
          </div>
        </div>
        <div className="co-hint">No payment is taken. Confirming deducts stock and sends the order confirmation emails, like a storefront order.</div>
      </>
    );
  };

  const renderFooter = () => {
    if (step === 3) {
      return (
        <button type="button" className="btn-primary-modern" onClick={toggle}>
          Done
        </button>
      );
    }
    return (
      <>
        <div className="co-footer-left">
          {step === 0 && cartItems.length > 0 && (
            <span>
              {cartItems.length} item{cartItems.length === 1 ? "" : "s"} · <strong>{money(cartTotal)}</strong>
            </span>
          )}
        </div>
        {step > 0 && (
          <button type="button" className="btn-secondary" onClick={() => setStep(step - 1)} disabled={busy}>
            Back
          </button>
        )}
        {step === 0 && (
          <button
            type="button"
            className="btn-primary-modern"
            onClick={() => setStep(1)}
            disabled={busy || !mainCartItem}
            title={mainCartItem ? "" : "Add a system to continue"}
          >
            Next
          </button>
        )}
        {step === 1 && (
          <button type="button" className="btn-primary-modern" onClick={makeOrder} disabled={busy}>
            {busy ? "Saving..." : "Next"}
          </button>
        )}
        {step === 2 && (
          <button type="button" className="btn-primary-modern" onClick={confirmOrder} disabled={busy}>
            {busy ? "Confirming..." : "Confirm Order"}
          </button>
        )}
      </>
    );
  };

  return (
    <Modal isOpen={isOpen} toggle={handleClose} centered size="xl" className="create-order-modal">
      <ModalHeader className="modal-header-custom" toggle={handleClose}>
        Create Order
      </ModalHeader>
      <ModalBody className="modal-body-custom">
        <div className="co-stepper">
          {STEPS.map((label, index) => (
            <div
              key={label}
              className={`co-step ${index === step ? "is-active" : ""} ${index < step || step === 3 ? "is-done" : ""}`}
            >
              <span className="co-step-number">{index < step || step === 3 ? <i className="fas fa-check"></i> : index + 1}</span>
              <span className="co-step-label">{label}</span>
            </div>
          ))}
        </div>
        {renderBody()}
      </ModalBody>
      <ModalFooter className="modal-footer-custom co-footer">{renderFooter()}</ModalFooter>

      {/* Errors open in their own popup on top of the order modal */}
      <Modal isOpen={!!error} toggle={() => setError("")} centered className="co-error-modal">
        <ModalHeader className="modal-header-custom" toggle={() => setError("")}>
          <i className="fas fa-exclamation-circle"></i> Unable to continue
        </ModalHeader>
        <ModalBody className="modal-body-custom">{error}</ModalBody>
        <ModalFooter className="modal-footer-custom co-footer">
          <button type="button" className="btn-primary-modern" onClick={() => setError("")}>
            OK
          </button>
        </ModalFooter>
      </Modal>
    </Modal>
  );
};

export default CreateOrderModal;
